import { useEffect, useRef } from 'react';
import { useV3Shell } from './V3ShellContext';
import { getPageZoom } from '../utils/desktopZoomFix';

// Turns every old-style pop-up into a V3 window, as it opens, without touching the file that draws it.
//
// An old pop-up is a fixed layer that centres ONE panel over a dimmed page (the app's standard shape: `fixed ... flex items-center justify-center bg-black/..`).
// A window that was rebuilt for V3 calls useV3Window, which gives its layer the V3 window class, theme, position and a width, and tells the shell how much room it
// wants. This does exactly the same to an old layer from the outside: the same classes and position, the width it is drawn at as its wanted width. The stylesheet then
// does the rest (the space between the bars, the blurred glass behind it, the grey header with the orange line, the red close button, the dark colours, the standard
// input boxes). Layers that are not windows are left alone: a spinner, a picture viewer, a menu, a toast, or a layer inside a window that already has the look.
// Nothing is changed in the layer's own markup, so the window works exactly as before.

const SKIP_INSIDE = '.v3-modal-region, [data-v3-keep]';
const LAYER_SELECTOR = 'div.fixed.items-center.justify-center';
const DIM_BACKDROP = /^bg-(black|gray-900|slate-900|zinc-900|neutral-900|stone-900)(\/\d+)?$/;
const PANEL_FILL = /^(bg-white|bg-gradient-to-[a-z]+|bg-gray-50|bg-slate-50|bg-card|bg-background)(\/\d+)?$/;
const MIN_PANEL_WIDTH = 280;
const MIN_PANEL_HEIGHT = 120;
const MIN_WANT = 420;
const MAX_WANT = 1700;

const classTokens = (element) => (typeof element.className === 'string' ? element.className : '').split(/\s+/).filter(Boolean);

// Is this layer an old pop-up window?
export const isOldWindowLayer = (layer) => {
  if (!layer || layer.nodeType !== 1 || layer.tagName !== 'DIV') return false;
  if (layer.closest(SKIP_INSIDE)) return false;
  const tokens = classTokens(layer);
  if (!tokens.includes('fixed') || !tokens.includes('items-center') || !tokens.includes('justify-center')) return false;
  if (!tokens.some((token) => DIM_BACKDROP.test(token))) return false;
  if (layer.children.length !== 1) return false;
  const panel = layer.firstElementChild;
  if (['IMG', 'VIDEO', 'IFRAME', 'CANVAS', 'SVG', 'EMBED', 'OBJECT'].includes(panel.tagName.toUpperCase())) return false; // a picture or video viewer, not a window
  const panelTokens = classTokens(panel);
  const filled = panelTokens.some((token) => PANEL_FILL.test(token)) || !!(panel.style && (panel.style.background || panel.style.backgroundColor));
  if (!filled) return false;
  const rect = panel.getBoundingClientRect();
  return rect.width >= MIN_PANEL_WIDTH && rect.height >= MIN_PANEL_HEIGHT;
};

// Starts watching the page. `getShell()` returns the V3 shell value (theme, regionVars, regionTop, registerModal). Returns { refresh, stop }.
export const startLegacyWindows = (getShell, getZoom = () => 1) => {
  const entries = new Map();
  let counter = 0;

  const apply = (entry) => {
    const shell = getShell();
    if (!shell) return;
    const { layer } = entry;
    layer.classList.add('v3-root', 'v3-modal-region', 'v3-modal-region--single');
    layer.setAttribute('data-v3-adopted', 'layer');
    layer.setAttribute('data-v3-theme', shell.theme);
    Object.entries(shell.regionVars || {}).forEach(([name, value]) => layer.style.setProperty(name, value));
    layer.style.setProperty('--v3-win-w', `${entry.want}px`);
    layer.style.setProperty('top', `${shell.regionTop}px`, 'important');
    layer.style.setProperty('z-index', '40', 'important');
    // Our own changes are not news: only a change made by the window's own code (it re-renders its class or style) should bring us back here.
    if (entry.observer) entry.observer.takeRecords();
  };

  const release = (entry) => {
    if (entry.observer) entry.observer.disconnect();
    if (entry.unregister) entry.unregister();
    entries.delete(entry.layer);
  };

  const adopt = (layer) => {
    const shell = getShell();
    if (!shell) return;
    const zoom = getZoom() || 1;
    const width = layer.firstElementChild.getBoundingClientRect().width / zoom;
    const entry = { layer, want: Math.max(MIN_WANT, Math.min(MAX_WANT, Math.round(width))), unregister: null, observer: null };
    counter += 1;
    entry.unregister = shell.registerModal(`legacy-window-${counter}`, entry.want);
    entries.set(layer, entry);
    // The old layer was drawn at the edges of the screen a moment ago; do not let the window slide from there to its place between the bars.
    layer.style.setProperty('transition', 'none', 'important');
    apply(entry);
    entry.observer = new MutationObserver(() => apply(entry));
    entry.observer.observe(layer, { attributes: true, attributeFilter: ['class', 'style'] });
    requestAnimationFrame(() => requestAnimationFrame(() => layer.style.removeProperty('transition')));
  };

  const scan = () => {
    entries.forEach((entry) => { if (!document.body.contains(entry.layer)) release(entry); });
    document.querySelectorAll(LAYER_SELECTOR).forEach((layer) => {
      if (!entries.has(layer) && isOldWindowLayer(layer)) adopt(layer);
    });
  };
  // Runs as soon as the page changes (before the browser paints), so an old window is never seen for a moment un-converted.
  const watcher = new MutationObserver(scan);
  watcher.observe(document.body, { childList: true, subtree: true });
  scan();

  return {
    // The shell's theme, regions or start position changed: bring every window up to date.
    refresh: () => entries.forEach(apply),
    stop: () => {
      watcher.disconnect();
      Array.from(entries.values()).forEach(release);
    },
  };
};

// Mounted once inside the V3 shell (next to the window host).
const V3LegacyWindows = () => {
  const shell = useV3Shell();
  const shellRef = useRef(shell);
  const watcherRef = useRef(null);
  shellRef.current = shell;

  useEffect(() => {
    watcherRef.current = startLegacyWindows(() => shellRef.current, getPageZoom);
    return () => { if (watcherRef.current) watcherRef.current.stop(); watcherRef.current = null; };
  }, []);

  useEffect(() => {
    if (watcherRef.current) watcherRef.current.refresh();
  }, [shell]);

  return null;
};

export default V3LegacyWindows;
